// react
import { useCallback, useMemo, useState } from 'react';

// mui
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Container,
	Divider,
	Grid,
	MenuItem,
	Paper,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StorageIcon from '@mui/icons-material/Storage';
import DatasetIcon from '@mui/icons-material/Inventory2';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// layouts
import { Authenticated } from '../../layouts/Authenticated/Authenticated';

// Types

interface DatasetMeta {
	description?: string;
	created?: string;
	story?: number;
}

interface DatasetInfo {
	name: string;
	meta: DatasetMeta;
	sections: Record<string, number>;
	totalItems: number;
}

interface IngestResult {
	message?: string;
	idMap?: Record<string, Record<string, number>>;
	summary?: Record<string, { success: number; failed: number }>;
	results?: Array<{ entityType: string; datasetId?: number; realId?: number; error?: string }>;
}

interface ProjectOption {
	id: number;
	name: string;
	idWbg: string;
	version: string;
	country?: { name: string } | null;
}

// Helpers

const V2_BASE = process.env.REACT_APP_API_URL?.replace(/\/v1\/?$/, '') || '';

const getAuthHeaders = () => {
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');
	return {
		'Content-Type': 'application/json',
		Authorization: `Bearer ${accessToken}`,
		User: String(idToken),
	};
};

const ENTITY_ORDER = [
	'components',
	'categories',
	'expenseTypes',
	'v1Contracts',
	'entityContracts',
	'workPlans',
	'activities',
	'v2Contracts',
	'conditions',
	'spendPlans',
	'vouchers',
	'invoices',
];

const SECTION_COLORS: Record<string, string> = {
	workPlans: '#1976d2',
	activities: '#2e7d32',
	v2Contracts: '#ed6c02',
	conditions: '#9c27b0',
	spendPlans: '#0288d1',
	vouchers: '#d32f2f',
	invoices: '#7b1fa2',
	components: '#616161',
	categories: '#616161',
	expenseTypes: '#616161',
	v1Contracts: '#616161',
	entityContracts: '#616161',
};

// Dataset Card

const DatasetCard = ({
	dataset,
	isActive,
	isIngesting,
	onIngest,
}: {
	dataset: DatasetInfo;
	isActive: boolean;
	isIngesting: boolean;
	onIngest: (name: string) => void;
}) => {
	const sortedSections = useMemo(
		() =>
			ENTITY_ORDER.filter((s) => dataset.sections[s] > 0).map((s) => ({
				name: s,
				count: dataset.sections[s],
			})),
		[dataset.sections]
	);

	return (
		<Paper
			elevation={isActive ? 4 : 0}
			sx={{
				p: 3,
				border: '1px solid',
				borderColor: isActive ? 'success.main' : 'divider',
				borderRadius: 3,
				position: 'relative',
			}}>
			{isActive && (
				<Chip
					label="Active"
					color="success"
					size="small"
					sx={{ position: 'absolute', top: 12, right: 12 }}
				/>
			)}

			<Stack spacing={2}>
				<Box>
					<Typography variant="h6" fontWeight={700}>
						{dataset.name}
					</Typography>
					{dataset.meta.description && (
						<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
							{dataset.meta.description}
						</Typography>
					)}
					{dataset.meta.story && (
						<Typography variant="caption" color="text.secondary">
							Story #{dataset.meta.story}
							{dataset.meta.created && ` · ${dataset.meta.created}`}
						</Typography>
					)}
				</Box>

				<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
					{sortedSections.map(({ name, count }) => (
						<Chip
							key={name}
							label={`${count} ${name}`}
							size="small"
							variant="outlined"
							sx={{
								borderColor: SECTION_COLORS[name] || '#616161',
								color: SECTION_COLORS[name] || '#616161',
								fontWeight: 500,
								fontSize: '0.75rem',
							}}
						/>
					))}
				</Box>

				<Typography variant="body2" fontWeight={600}>
					{dataset.totalItems} total items
				</Typography>

				<Button
					variant={isActive ? 'outlined' : 'contained'}
					startIcon={isIngesting ? <CircularProgress size={16} /> : <PlayArrowIcon />}
					disabled={isIngesting}
					onClick={() => onIngest(dataset.name)}
					size="small">
					{isIngesting ? 'Ingesting...' : 'Ingest'}
				</Button>
			</Stack>
		</Paper>
	);
};

// Results Panel

const ResultsPanel = ({ result }: { result: IngestResult }) => {
	const hasErrors = result.results?.some((r) => r.error);

	return (
		<Paper sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
			<Stack spacing={2}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					{hasErrors ? <ErrorIcon color="error" /> : <CheckCircleIcon color="success" />}
					<Typography variant="h6" fontWeight={700}>
						{result.message ||
							(hasErrors ? 'Ingestion completed with errors' : 'Ingestion complete')}
					</Typography>
				</Box>

				{/* Summary counts */}
				{result.summary && (
					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
						{Object.entries(result.summary).map(([type, counts]) => (
							<Chip
								key={type}
								label={`${type}: ${counts.success} created${counts.failed ? `, ${counts.failed} failed` : ''}`}
								color={counts.failed ? 'error' : 'success'}
								variant="outlined"
								size="small"
							/>
						))}
					</Box>
				)}

				{/* ID Mapping table */}
				{result.idMap && Object.keys(result.idMap).length > 0 && (
					<Box>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							ID Mapping (dataset → real)
						</Typography>
						<TableContainer>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell sx={{ fontWeight: 600 }}>Entity Type</TableCell>
										<TableCell sx={{ fontWeight: 600 }}>Dataset ID</TableCell>
										<TableCell sx={{ fontWeight: 600 }}>Real ID</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{Object.entries(result.idMap).flatMap(([type, map]) =>
										Object.entries(map).map(([dsId, realId]) => (
											<TableRow key={`${type}-${dsId}`}>
												<TableCell>{type}</TableCell>
												<TableCell sx={{ fontFamily: 'monospace' }}>{dsId}</TableCell>
												<TableCell
													sx={{ fontFamily: 'monospace', color: 'success.main', fontWeight: 600 }}>
													{realId}
												</TableCell>
											</TableRow>
										))
									)}
								</TableBody>
							</Table>
						</TableContainer>
					</Box>
				)}

				{/* Errors */}
				{result.results
					?.filter((r) => r.error)
					.map((r, i) => (
						<Alert key={i} severity="error" variant="outlined">
							{r.entityType}
							{r.datasetId ? ` #${r.datasetId}` : ''}: {r.error}
						</Alert>
					))}
			</Stack>
		</Paper>
	);
};

// Page: Admin Datasets

export const PageAdminDatasets = () => {
	const [datasets, setDatasets] = useState<DatasetInfo[]>([]);
	const [activeDataset, setActiveDataset] = useState<string | null>(null);
	const [ingestingDataset, setIngestingDataset] = useState<string | null>(null);
	const [ingestResult, setIngestResult] = useState<IngestResult | null>(null);
	const [ingestError, setIngestError] = useState<string | null>(null);
	const [projectId, setProjectId] = useState<number>(0);
	const [projects, setProjects] = useState<ProjectOption[]>([]);

	// Fetch available projects
	const { isLoading: isLoadingProjects } = useFetch({
		url: `${process.env.REACT_APP_API_URL}/projects?fields=idWbg,version,name&populate=country&limit=100&orderBy=name&order=ASC`,
		options: { headers: getAuthHeaders() },
		onSuccess: (res) => {
			const list: ProjectOption[] = (res.data || []).map(
				(p: {
					id: number;
					name?: string;
					title?: string;
					idWbg?: string;
					id_wbg?: string;
					version?: string;
					country?: string;
				}) => ({
					id: p.id,
					name: p.name,
					idWbg: p.idWbg || p.id_wbg || '',
					version: p.version ?? '1',
					country: p.country,
				})
			);
			setProjects(list);
			if (list.length > 0 && projectId === 0) setProjectId(list[0].id);
		},
		onError: () => setProjects([]),
	});

	const selectedProject = useMemo(
		() => projects.find((p) => p.id === projectId) || null,
		[projects, projectId]
	);

	// Fetch available datasets from the API
	const { isLoading, refetch } = useFetch({
		url: `${V2_BASE}/v2/datasets`,
		options: { headers: getAuthHeaders() },
		onSuccess: (res) => {
			setDatasets(res.data?.datasets || []);
			if (res.data?.active) setActiveDataset(res.data.active);
		},
		onError: () => {
			// Fallback: if no /v2/datasets endpoint yet, show empty state
			setDatasets([]);
		},
	});

	// Ingest a dataset
	const handleIngest = useCallback(
		async (datasetName: string) => {
			setIngestingDataset(datasetName);
			setIngestResult(null);
			setIngestError(null);

			try {
				const response = await fetch(`${V2_BASE}/v2/ingest`, {
					method: 'POST',
					headers: getAuthHeaders(),
					body: JSON.stringify({
						projectId,
						datasetName,
					}),
				});

				const result = await response.json();

				if (result.error) {
					setIngestError(result.error.message || 'Ingestion failed');
					if (result.data) setIngestResult(result.data);
				} else {
					setIngestResult(result.data);
					setActiveDataset(datasetName);
				}
			} catch (err) {
				setIngestError(err instanceof Error ? err.message : 'Network error');
			} finally {
				setIngestingDataset(null);
			}
		},
		[projectId]
	);

	return (
		<Authenticated>
			<Container maxWidth="xl" sx={{ py: 4 }}>
				<Stack spacing={4}>
					{/* Header */}
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
							<DatasetIcon sx={{ fontSize: 32, color: 'primary.main' }} />
							<Box>
								<Typography variant="h4" fontWeight={700}>
									Dataset Manager
								</Typography>
								<Typography variant="body2" color="text.secondary">
									Load test datasets into the application via bulk upload service functions
								</Typography>
							</Box>
						</Box>
						<Stack direction="row" spacing={1}>
							<Button
								variant="outlined"
								startIcon={isLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
								onClick={() => refetch()}
								disabled={isLoading}>
								Refresh
							</Button>
						</Stack>
					</Box>

					<Divider />

					{/* Project selector */}
					<Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
						<Stack spacing={1.5}>
							<Stack direction="row" spacing={2} alignItems="center">
								<StorageIcon color="action" />
								<Typography variant="body2" fontWeight={600}>
									Target Project:
								</Typography>
								{isLoadingProjects ? (
									<CircularProgress size={20} />
								) : projects.length === 0 ? (
									<Typography variant="body2" color="text.secondary">
										No projects available
									</Typography>
								) : (
									<Select
										value={projectId}
										onChange={(e) => setProjectId(Number(e.target.value))}
										size="small"
										sx={{ minWidth: 360 }}>
										{projects.map((p) => (
											<MenuItem key={p.id} value={p.id}>
												<Stack direction="row" spacing={1} alignItems="center">
													<Typography variant="body2" fontWeight={600}>
														{p.name}
													</Typography>
													<Typography variant="caption" color="text.secondary">
														{p.idWbg}
													</Typography>
													{p.version === '2' && (
														<Chip
															label="V2"
															size="small"
															color="primary"
															sx={{ height: 20, fontSize: '0.7rem' }}
														/>
													)}
													{p.country?.name && (
														<Typography variant="caption" color="text.secondary">
															· {p.country.name}
														</Typography>
													)}
												</Stack>
											</MenuItem>
										))}
									</Select>
								)}
							</Stack>
							{selectedProject && (
								<Typography variant="caption" color="text.secondary" sx={{ pl: 5 }}>
									ID: {selectedProject.id} · {selectedProject.idWbg}
									{selectedProject.country?.name ? ` · ${selectedProject.country.name}` : ''}
									{selectedProject.version === '2' ? ' · V2 Enabled' : ' · V1 Only'}
								</Typography>
							)}
							<Typography variant="caption" color="text.secondary" sx={{ pl: 5 }}>
								Project must be onboarded with entities, accounts, and users before ingesting.
							</Typography>
						</Stack>
					</Paper>

					{/* Dataset grid */}
					{isLoading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
							<CircularProgress />
						</Box>
					) : datasets.length === 0 ? (
						<Alert severity="info">
							No datasets found. Add JSON files to <code>bldt-db/datasets/</code> and create a{' '}
							<code>GET /v2/datasets</code> endpoint to list them, or use the CLI:{' '}
							<code>node ingest.js --dataset minimal --project-id 1</code>
						</Alert>
					) : (
						<Grid container spacing={3}>
							{datasets.map((ds) => (
								<Grid item xs={12} md={6} lg={4} key={ds.name}>
									<DatasetCard
										dataset={ds}
										isActive={activeDataset === ds.name}
										isIngesting={ingestingDataset === ds.name}
										onIngest={handleIngest}
									/>
								</Grid>
							))}
						</Grid>
					)}

					{/* Results */}
					{ingestError && (
						<Alert severity="error" variant="filled">
							{ingestError}
						</Alert>
					)}

					{ingestResult && <ResultsPanel result={ingestResult} />}
				</Stack>
			</Container>
		</Authenticated>
	);
};
